import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Crown, TrendingUp, Upload, X, Trash2, CheckSquare, Square } from 'lucide-react';
import MonitorCard from './MonitorCard';
import MonitorForm from './MonitorForm';
import BulkMonitorUpload from './BulkMonitorUpload';
import Pagination from './Pagination';
import { formatRelativeTime, formatDateInUserTimezone } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonitorList = ({ token, filterStatus = null }) => {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Actual search query sent to backend
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [user, setUser] = useState(null);
  const [forceCheckLoading, setForceCheckLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(12);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchDebounceTimer = useRef(null);
  
  // Determine status filter from route
  const statusFilter = filterStatus || 'all';

  useEffect(() => {
    fetchMonitors();
    fetchSubscriptionLimits();
    fetchUser();
  }, [currentPage, filterStatus, searchQuery]);

  // Reset bulk-selection when the visible page / filter / search changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, filterStatus, searchQuery]);

  // Debounced search - wait 500ms after user stops typing
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // Set new timer
    searchDebounceTimer.current = setTimeout(() => {
      setSearchQuery(searchTerm);
      setCurrentPage(1); // Reset to first page on new search
    }, 500);

    // Cleanup
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchTerm]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

  const forceCheckMonitors = async () => {
    // Confirm if there are many monitors
    if (totalItems > 50) {
      const confirmed = window.confirm(
        `You have ${totalItems} monitors. Force checking all of them may take several minutes and could trigger rate limiting on some sites. Continue?`
      );
      if (!confirmed) return;
    }
    
    setForceCheckLoading(true);
    setError('');
    
    try {
      // Send request with a reasonable limit (50 at once)
      const limit = Math.min(totalItems, 50);
      const response = await axios.post(
        `${API}/monitors/force-check?limit=${limit}`, 
        {}, 
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000 // 3 minutes timeout
        }
      );
      
      console.log('Force check result:', response.data);
      
      // Show summary
      const { total, successful, failed } = response.data;
      if (failed > 0) {
        setError(`Checked ${total} monitors: ${successful} successful, ${failed} failed`);
      }
      
      // Refresh monitors to show updated timestamps
      await fetchMonitors();
      
    } catch (err) {
      console.error('Force check failed:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Force check timed out. Some monitors may still be checking in the background.');
      } else {
        setError(err.response?.data?.detail || 'Failed to update monitor status');
      }
    } finally {
      setForceCheckLoading(false);
    }
  };

  const fetchSubscriptionLimits = async () => {
    try {
      const response = await axios.get(`${API}/subscription/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionLimits(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription limits:', err);
    }
  };

  const fetchMonitors = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      
      // Add search query if present
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await axios.get(`${API}/monitors`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setMonitors(response.data.monitors);
      setTotalPages(response.data.pages);
      setTotalItems(response.data.total);
    } catch (err) {
      setError('Failed to load monitors');
      console.error('Monitors error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCreate = async (monitorData) => {
    try {
      const response = await axios.post(`${API}/monitors`, monitorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors([...monitors, response.data]);
      setShowForm(false);
      // Refresh subscription limits after creating a monitor
      fetchSubscriptionLimits();
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to create monitor');
    }
  };

  const handleUpdate = async (monitorId, monitorData) => {
    try {
      const response = await axios.put(`${API}/monitors/${monitorId}`, monitorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.map(m => m.id === monitorId ? response.data : m));
      setEditingMonitor(null);
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update monitor');
    }
  };

  const handleDelete = async (monitor) => {
    if (!window.confirm(`Are you sure you want to delete "${monitor.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/monitors/${monitor.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.filter(m => m.id !== monitor.id));
      // Refresh subscription limits after deleting a monitor
      fetchSubscriptionLimits();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete monitor');
    }
  };

  const handleToggleStatus = async (monitor) => {
    try {
      const newStatus = monitor.status === 'active' ? 'paused' : 'active';
      const response = await axios.put(`${API}/monitors/${monitor.id}`, 
        { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(monitors.map(m => m.id === monitor.id ? response.data : m));
    } catch (err) {
      console.error('Status toggle error:', err);
      alert('Failed to update monitor status');
    }
  };

  const handleBulkUploadComplete = () => {
    setShowBulkUpload(false);
    fetchMonitors();
    fetchSubscriptionLimits();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Immediate search on Enter key
      setSearchQuery(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Bulk selection helpers
  const toggleSelect = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const allOnPageSelected = monitors.length > 0 && monitors.every((m) => selectedIds.has(m.id));
  const someOnPageSelected = monitors.some((m) => selectedIds.has(m.id));

  const togglePageSelect = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        monitors.forEach((m) => next.delete(m.id));
      } else {
        monitors.forEach((m) => next.add(m.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async (mode) => {
    // mode: 'selected' (only the IDs in selectedIds) or 'all' (every monitor matching current filters)
    const isAll = mode === 'all';
    const count = isAll ? totalItems : selectedIds.size;
    if (count === 0) return;

    const label = isAll ? `ALL ${count} monitors matching the current filter` : `${count} selected monitor${count === 1 ? '' : 's'}`;
    if (!window.confirm(`Delete ${label}? This will also remove related incidents and history. This action cannot be undone.`)) {
      return;
    }

    setBulkDeleteLoading(true);
    setError('');
    try {
      const body = isAll
        ? {
            all: true,
            status_filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchQuery || undefined,
          }
        : { ids: Array.from(selectedIds) };

      const response = await axios.post(`${API}/monitors/bulk-delete`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });

      clearSelection();

      // If the current page becomes empty, step back one page
      if (currentPage > 1 && response.data.deleted >= monitors.length) {
        setCurrentPage((p) => Math.max(1, p - 1));
      } else {
        await fetchMonitors();
      }
      fetchSubscriptionLimits();
    } catch (err) {
      console.error('Bulk delete error:', err);
      setError(err.response?.data?.detail || 'Failed to bulk delete monitors');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // No need for client-side filtering - backend handles search
  const displayMonitors = monitors;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-48 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Monitors</h2>
          <p className="text-gray-600 text-sm sm:text-base">Manage your website monitoring</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={forceCheckMonitors}
            disabled={forceCheckLoading}
            className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm sm:text-base"
          >
            {forceCheckLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Checking...</span>
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4" />
                <span>Refresh Status</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowBulkUpload(true)}
            className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
          >
            <Upload className="w-4 h-4" />
            <span>Bulk Upload</span>
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <Plus className="w-4 h-4" />
            <span>Add Monitor</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search monitors by name or URL... (Press Enter or wait 500ms)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="w-full pl-9 sm:pl-10 pr-10 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm sm:text-base"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear search"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          {searchQuery && searchQuery !== searchTerm && (
            <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        
        {/* Status Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/app/monitors')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              statusFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => navigate('/app/monitors/online')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              statusFilter === 'online'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Online
          </button>
          <button
            onClick={() => navigate('/app/monitors/offline')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              statusFilter === 'offline'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Offline
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
          {error}
        </div>
      )}

      {/* Subscription Status Banner */}
      {subscriptionLimits && (
        <div className="mb-6">
          {subscriptionLimits.at_limit ? (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-1">Monitor Limit Reached</h3>
                  <p className="text-amber-700 text-sm mb-3">
                    You're using all {subscriptionLimits.limits.monitors} monitors on your {subscriptionLimits.tier} plan. 
                    Upgrade to monitor more websites and get advanced features.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    <button
                      onClick={() => window.location.href = '/settings?section=subscription'}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Crown className="w-4 h-4 inline mr-2" />
                      Upgrade Plan
                    </button>
                    <button
                      onClick={() => window.location.href = '/settings?section=subscription'}
                      className="bg-white text-blue-600 border border-blue-200 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      View Pricing
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : subscriptionLimits.usage.monitors >= subscriptionLimits.limits.monitors * 0.8 ? (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-800 mb-1">Approaching Monitor Limit</h3>
                  <p className="text-blue-700 text-sm mb-3">
                    You're using {subscriptionLimits.usage.monitors} of {subscriptionLimits.limits.monitors} monitors on your {subscriptionLimits.tier} plan. 
                    Consider upgrading to monitor more websites.
                  </p>
                  <button
                    onClick={() => window.location.href = '/settings?section=subscription'}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    View Upgrade Options
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Bulk Action Bar */}
      {monitors.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <button
              onClick={togglePageSelect}
              className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              data-testid="select-all-page-btn"
            >
              {allOnPageSelected ? (
                <CheckSquare className="w-4 h-4 text-blue-600" />
              ) : someOnPageSelected ? (
                <CheckSquare className="w-4 h-4 text-blue-400" />
              ) : (
                <Square className="w-4 h-4 text-gray-500" />
              )}
              <span>{allOnPageSelected ? 'Deselect page' : 'Select page'}</span>
            </button>
            {selectedIds.size > 0 && (
              <span className="text-sm text-gray-600" data-testid="selected-count">
                {selectedIds.size} selected
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  data-testid="clear-selection-btn"
                >
                  Clear
                </button>
                <button
                  onClick={() => handleBulkDelete('selected')}
                  disabled={bulkDeleteLoading}
                  className="inline-flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  data-testid="bulk-delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete {selectedIds.size} selected</span>
                </button>
              </>
            )}
            {totalItems > 0 && (
              <button
                onClick={() => handleBulkDelete('all')}
                disabled={bulkDeleteLoading}
                className="inline-flex items-center space-x-2 px-3 py-1.5 bg-white text-red-600 border border-red-200 text-sm font-medium rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                data-testid="bulk-delete-all-btn"
                title={`Delete every monitor matching the current ${statusFilter !== 'all' || searchQuery ? 'filter' : 'list'}`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete all{statusFilter !== 'all' || searchQuery ? ' matching' : ''} ({totalItems})</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Monitors Grid */}
      {displayMonitors.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="bg-gray-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No monitors yet</h3>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">Add your first website to start monitoring</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            Add Your First Monitor
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {displayMonitors.map((monitor) => (
            <MonitorCard
              key={monitor.id}
              monitor={monitor}
              onEdit={setEditingMonitor}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              selected={selectedIds.has(monitor.id)}
              onSelectChange={toggleSelect}
            />
          ))}
        </div>
      )}

      {/* Search Results Info & Pagination */}
      {monitors.length > 0 && (
        <div className="mt-8">
          {searchQuery && (
            <div className="mb-4 text-center">
              <p className="text-sm text-gray-600">
                Found <span className="font-semibold text-blue-600">{totalItems}</span> monitor{totalItems !== 1 ? 's' : ''} matching "<span className="font-medium">{searchQuery}</span>"
              </p>
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            className="rounded-lg shadow-sm border border-gray-200"
          />
        </div>
      )}

      {/* Monitor Form Modal */}
      {(showForm || editingMonitor) && (
        <MonitorForm
          monitor={editingMonitor}
          token={token}
          onSubmit={editingMonitor ? 
            (data) => handleUpdate(editingMonitor.id, data) : 
            handleCreate
          }
          onClose={() => {
            setShowForm(false);
            setEditingMonitor(null);
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkMonitorUpload
          user={user}
          onSuccess={() => {
            fetchMonitors();
            fetchSubscriptionLimits();
          }}
          onClose={() => setShowBulkUpload(false)}
        />
      )}
    </div>
  );
};

export default MonitorList;
